import { Injectable, OnDestroy } from '@angular/core';
import { JobSchedulerIdbService } from '@pwa/indexed-db/services/static-data/job-scheduler-idb.service';
import { Subject, takeUntil } from 'rxjs';
import { ApplicationFacade } from '@app/store/facade/application.facade';
import { JobScheduler } from '@shared/models/offline.model';
import { translate } from '@jsverse/transloco';
import { MasterDataSynchronizationService } from '@pwa/services/synchronization/static-data/master-data-synchronization.service';
import { HeaderConfigService } from '@app/modules/project-details/core/services/header-config.service';
import { SnackbarService } from '@shared/services/snackbar/snackbar.service';
import { DialogService } from '@shared/services/dialog/dialog.service';

/**
 * Service responsible for checking when master data was last updated and prompting the User to refresh the outdated
 * offline master data.
 *
 * Flow:
 * - Reads the last update date from IndexedDB;
 * - Checks if data is older than 14 days;
 * - Ensures the device is online (can't update in offline mode, duh);
 * - Shows a confirmation dialog if the following two conditions are met:
 *    1. There is no `nextPromptAt` or it is less than 24 hours from the current date and time;
 *    2. The last update was more than 14 days ago.
 * - The dialog has two outcomes:
 *  - Performs synchronization on user confirmation;
 *  - Defers the reminder dialog by 24 hours on postpone.
 */
@Injectable({
  providedIn: 'root',
})
export class MasterDataUpdateSchedulerService implements OnDestroy {
  isOnline: boolean;

  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private applicationFacade: ApplicationFacade,
    private jobSchedulerIdbService: JobSchedulerIdbService,
    private dialogService: DialogService,
    private headerConfigService: HeaderConfigService,
    private snackbarService: SnackbarService,
    private masterDataSynchronizationService: MasterDataSynchronizationService
  ) {
    this.applicationFacade.isOnline$.pipe(takeUntil(this.onDestroy$)).subscribe((isOnline: boolean) => {
      this.isOnline = isOnline;
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Checks whether the master data update is outdated and, if conditions are met,
   * prompts the user with a dialog asking to refresh the data.
   *
   * Conditions required to show dialog:
   *  - Job scheduler entry must exist;
   *  - User must be online;
   *  - `lastUpdated` older than 14 days;
   *  - `nextPromptAt` is not set or has passed.
   *
   * On User confirmation:
   *  - Shows loading spinner;
   *  - Executes synchronization via the MasterDataSynchronizationService;
   *  - Shows success/error snack messages.
   *
   * On User cancellation (postpone):
   *  - Defers the next prompt by storing `nextPromptAt = currentDate + 24hrs`.
   */
  async checkLastUpdateDateAndPromptIfNeeded(): Promise<void> {
    const jobSchedulerData: JobScheduler | undefined = await this.jobSchedulerIdbService.getMasterDataSynchronizationJobData();

    if (!this.isOnline || !jobSchedulerData) return;

    const lastUpdatedDate: Date = jobSchedulerData.lastUpdated;
    const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
    const nextPromptAt: Date | undefined = jobSchedulerData.nextPromptAt;
    const currentDate = new Date();

    const isOutdated: boolean = lastUpdatedDate < fourteenDaysAgo;
    const canShowDialog: boolean = !nextPromptAt || nextPromptAt <= currentDate;

    if (isOutdated && canShowDialog) {
      this.dialogService
        .openConfirmationDialog({
          title: translate('dialogs.update-master-data-title'),
          message: translate('dialogs.update-master-data-message'),
          confirmActionLabel: translate('dialogs.update-master-data-confirm-action'),
          cancelActionLabel: translate('dialogs.update-master-data-cancel-action'),
          disableClose: true,
        })
        .subscribe(async (confirmed: boolean) => {
          if (confirmed) {
            // On update: refresh the master data and remove the postpone date
            try {
              this.headerConfigService.shouldDisplaySpinner.next(true);
              await this.masterDataSynchronizationService.getMasterDataSynchronizationJobSchedulerAndSynchronize(lastUpdatedDate);
              this.snackbarService.handleSuccess(translate('snacks.synchronize-offline-master-data-successfully'));
            } catch (err) {
              this.snackbarService.handleError(translate('snacks.synchronize-offline-master-data-unsuccessfully'));
              console.error(translate('snacks.synchronize-offline-master-data-unsuccessfully'), err);
            } finally {
              this.headerConfigService.shouldDisplaySpinner.next(false);
            }
          } else {
            // On postpone: set the nextPromptAt to +=24hrs
            this.jobSchedulerIdbService.setNextUpdatePromptDialogDate(lastUpdatedDate);
          }
        });
    }
  }
}
